import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AuthService } from "../../../core/services/auth.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiLink,
  TuiLoader,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiStringifyContentPipe,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { TuiComboBoxModule, TuiInputDateRangeModule, TuiInputModule } from "@taiga-ui/legacy";
import { ClientService } from "../../../core/services/client.service";
import { ForgotResetPasswordService } from "../../../core/services/forgot-reset-password.service.service";
import { InvoiceService } from "../../../core/services/invoice.service";
import { ShiftService } from "../../../core/services/shift.service";
import { TuiDayRange } from "@taiga-ui/cdk";
import { IShift } from "../../../model/interface/shift";
import { ChargesService } from "../../../core/services/charges.service";
import { DisableUnlessPermissionDirective } from "../../../core/directives/disable-unless-permission.directive";
import { formatClientIdentifier } from "../../../shared/utils/client-identifier";
import { ChangeClientTypePanelComponent } from "../../../shared/components/change-client-type-panel/change-client-type-panel.component";
import { ClientTypeChangeFlowService } from "../../../shared/services/client-type-change-flow.service";
import {
  canUnarchiveClient,
  TYPE_CHANGE_UNARCHIVE_MESSAGE,
} from "../../../shared/utils/client-archive";
import {
  RecordRenewalConfirmPayload,
  RecordRenewalDialogComponent,
} from "../../../shared/components/record-renewal-dialog/record-renewal-dialog.component";

@Component({
  selector: "app-single-client",
  imports: [
    DisableUnlessPermissionDirective,
    FormsModule,
    NgForOf,
    NgIf,
    TuiAppearance,
    TuiDataListWrapper,
    TuiBreadcrumbs,
    TuiDataList,
    TuiDropdown,
    TuiFade,
    TuiTable,
    TuiLink,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    RouterLink,
    TuiDataListDropdownManager,
    TuiButton,
    TuiPagination,
    TuiComboBoxModule,
    TuiInputDateRangeModule,
    ChangeClientTypePanelComponent,
    RecordRenewalDialogComponent,
  ],
  templateUrl: "./single-client.component.html",
  styleUrl: "./single-client.component.scss",
  providers: [DatePipe],
})
export class SingleClientComponent {
  protected breadcrumbs: string[] = ["Royal Hospital", "Clients"];
  clientId: string = "";
  client: any | null = null;
  isLoading: boolean = false;
  private readonly dialogs = inject(TuiDialogService);
  private readonly alerts = inject(TuiAlertService);
  protected size: TuiSizeL | TuiSizeS = "s";
  protected open = false;
  invoiceData: any[] = [];
  filters = {
    searchText: "",
    status: null, // will be set to an object when selected
  };
  pageIndex = 1;
  pageSize = 5;
  total: number = 0;
  sortField = "createdAt";
  sortOrder: number = -1;

  shifts: IShift[] = [];
  shiftPageIndex = 1;
  shiftPageSize = 5;
  shiftTotal: number = 0;
  filterStartDate: string = "";
  filterEndDate: string = "";
  dateRange: TuiDayRange | null = null;

  chargesPageIndex = 1;
  chargesPageSize = 5;
  chargesTotal: number = 0;
  chargesRecords: any[] = [];

  protected readonly statusItems = [
    { status: "Draft", value: "Draft" },
    { status: "Sent", value: "Sent" },
  ];

  loggedInUserCanEdit = false;
  changeTypeDialogOpen = false;
  changeTypeBusy = false;
  renewDialogOpen = false;
  renewBusy = false;
  editRenewalRecord: {
    _id?: string;
    date?: string | Date;
    fee?: number;
    notes?: string;
  } | null = null;
  private readonly typeChangeFlow = inject(ClientTypeChangeFlowService);
  private authService = inject(AuthService);

  constructor(
    private clientService: ClientService,
    private invoiceService: InvoiceService,
    private router: Router,
    private route: ActivatedRoute,
    private resetPasswordService: ForgotResetPasswordService,
    private shiftService: ShiftService,
    private datePipe: DatePipe,
    private chargesService: ChargesService
  ) { }

  ngOnInit() {
    this.clientId = this.route.snapshot.paramMap.get("id")!;
    if (this.clientId) {
      this.loadClientData();
      this.loadInvoices();
    }
    this.loadShifts();
    this.loadChargesRecords();
    this.checkUserPermission();
  }

  loadClientData(): void {
    this.isLoading = true;
    if (this.clientId) {
      this.clientService
        .getOneClientById(this.clientId)
        .subscribe((res: any) => {
          this.client = res;
          this.breadcrumbs = [
            "Royal Hospital",
            "Clients",
            this.getClientIdentifier(this.client) || this.client?.name || "",
          ];
          this.isLoading = false;
        });
    }
  }

  /** System-wide formatted client id: REG-xxxx, NREG-xxxx, BHT-xxxxx */
  getClientIdentifier(client: any): string {
    return formatClientIdentifier(client);
  }

  /** Human-readable age combining years and months, e.g. "3 Years 6 Months". */
  formatAge(client: any): string {
    const parts: string[] = [];
    if (client?.age) parts.push(`${client.age} Years`);
    if (client?.ageMonths) parts.push(`${client.ageMonths} Months`);
    return parts.length ? parts.join(" ") : "-";
  }

  /**
   * Type-aware label for the registration / nursing / inward classification.
   */
  getRegistrationTypeLabel(client: any): string {
    if (!client) return "";
    if (client.clientType === "Inward") return "Inward";
    if (client.clientType === "Unregistered")
      return client.nursingType || "Non Registered";
    return client.registrationType || "";
  }

  /**
   * Type-aware "Registered on" label so Inward / Unregistered patients aren't
   * mislabeled as Registered.
   */
  getOnboardingLabel(client: any): string {
    if (!client) return "";
    const fmt = (d: any) =>
      d ? this.datePipe.transform(d, "MMM d, y") || "" : "";
    if (client.clientType === "Inward") {
      return client.inwardDate
        ? `Admitted on ${fmt(client.inwardDate)}`
        : "Inward client";
    }
    if (client.clientType === "Unregistered") {
      return client.startingDate
        ? `Started on ${fmt(client.startingDate)}`
        : "Non Registered client";
    }
    return client.registrationDate
      ? `Registered on ${fmt(client.registrationDate)}`
      : "";
  }

  get isRegistered(): boolean {
    return (this.client?.clientType ?? "Registered") === "Registered";
  }

  get isUnregistered(): boolean {
    return this.client?.clientType === "Unregistered";
  }

  get isInward(): boolean {
    return this.client?.clientType === "Inward";
  }

  get isOneYearRegistered(): boolean {
    return (
      this.isRegistered &&
      this.client?.registrationType === "One Year"
    );
  }

  get sortedRenewalFees(): any[] {
    const fees = this.client?.renewalFees;
    if (!Array.isArray(fees)) return [];
    return [...fees]
      .filter((r) => this.isDisplayableRenewalRecord(r))
      .sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
  }

  /** Hide phantom rows created by legacy client-edit renewal fee logic. */
  private isDisplayableRenewalRecord(record: any): boolean {
    const fee = Number(record?.fee);
    if (Number.isFinite(fee) && fee > 0) return true;
    if (record?.recordedAt) return true;
    if (String(record?.notes ?? "").trim()) return true;
    return false;
  }

  get membershipStatus(): "expired" | "expiring" | "active" | null {
    if (!this.isOneYearRegistered || !this.client?.expiryDate) return null;
    const expiry = new Date(this.client.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffDays = Math.ceil(
      (expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays < 0) return "expired";
    if (diffDays <= 7) return "expiring";
    return "active";
  }

  recordedByName(record: any): string {
    const u = record?.recordedBy;
    if (!u) return "—";
    if (typeof u === "object" && u.name) return u.name;
    return "—";
  }

  isCurrentRenewalRow(record: any): boolean {
    if (!record?.date || !this.client?.renewalDate) return false;
    const a = new Date(record.date);
    const b = new Date(this.client.renewalDate);
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  onRenewDialogOpenChange(open: boolean): void {
    this.renewDialogOpen = open;
    if (!open) {
      this.editRenewalRecord = null;
    }
  }

  openRenewDialog(): void {
    this.editRenewalRecord = null;
    this.renewDialogOpen = true;
  }

  openEditRenewalDialog(row: any): void {
    if (!row?._id) {
      this.alerts
        .open(
          "This renewal record cannot be edited (missing id). Record a new renewal or contact support.",
          { label: "Cannot edit", appearance: "warning" },
        )
        .subscribe();
      return;
    }
    this.editRenewalRecord = {
      _id: String(row._id),
      date: row.date,
      fee: row.fee,
      notes: row.notes,
    };
    this.renewDialogOpen = true;
  }

  canEditRenewalRow(row: any): boolean {
    return !!row?._id && !this.client?.archived && this.loggedInUserCanEdit;
  }

  onRenewConfirm(payload: RecordRenewalConfirmPayload): void {
    if (!this.clientId || this.renewBusy) return;
    this.renewBusy = true;

    const request$ = payload.renewalId
      ? this.clientService.updateRenewalRecord(
          this.clientId,
          payload.renewalId,
          payload,
        )
      : this.clientService.renewClient(this.clientId, payload);

    request$.subscribe({
      next: (res) => {
        this.renewBusy = false;
        this.renewDialogOpen = false;
        this.editRenewalRecord = null;
        if (res?.success) {
          this.alerts
            .open(
              res.message ||
                (payload.renewalId
                  ? "Renewal record updated successfully."
                  : "Renewal recorded successfully."),
              { label: "Success", appearance: "success" },
            )
            .subscribe();
          this.loadClientData();
        }
      },
      error: (err) => {
        this.renewBusy = false;
        const message =
          err?.error?.message ||
          err?.message ||
          (payload.renewalId
            ? "Failed to update renewal record."
            : "Failed to record renewal.");
        this.alerts
          .open(message, { label: "Error", appearance: "error" })
          .subscribe();
      },
    });
  }

  loadShifts(): void {
    const payload = {
      pageIndex: this.shiftPageIndex,
      pageSize: this.shiftPageSize,
      sortField: "date",
      sortOrder: -1,
      startDate: this.filterStartDate,
      endDate: this.filterEndDate,
    };

    this.shiftService
      .getPagedClientShift(payload, this.clientId)
      .subscribe((res: any) => {
        if (res && res.data && res.data.length) {
          const pagedResult = res.data[0];
          this.shifts = pagedResult.data;
          this.shiftTotal =
            (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.shifts = [];
          this.shiftTotal = 0;
        }
      });
  }

  onFilter(): void {
    if (this.dateRange) {
      const startDate = this.dateRange.from.toLocalNativeDate();
      const endDate = this.dateRange.to.toLocalNativeDate();

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);

      this.filterStartDate = startDate.toISOString();
      this.filterEndDate = endDate.toISOString();
    } else {
      this.filterStartDate = "";
      this.filterEndDate = "";
    }

    this.shiftPageIndex = 1;
    this.loadShifts();
  }

  resetFilter(): void {
    this.dateRange = null;
    this.filterStartDate = "";
    this.filterEndDate = "";
    this.shiftPageIndex = 1;
    this.loadShifts();
  }

  loadChargesRecords(): void {
    const payload = {
      pageIndex: this.chargesPageIndex,
      pageSize: this.chargesPageSize,
      sortField: "year",
      sortOrder: -1,
    };

    this.shiftService.getMonthlyShiftsByClientIdPaginated(this.clientId, payload).subscribe(
      (res: any) => {
        console.log("Paged charges records:", res.data);
        if (res.data && res.data[0]) {
          const pagedResult = res.data[0];
          this.chargesRecords = pagedResult.data;
          this.chargesTotal = (pagedResult.metadata && pagedResult.metadata[0]?.total) || 0;
        } else {
          this.chargesRecords = [];
          this.chargesTotal = 0;
        }
      },
      (error) => {
        console.error(error);
      }
    );
  }

  navigateTo(index: number): void {
    switch (index) {
      case 0:
        this.router.navigate(["admin/client"]);
        break;
      case 1:
        this.router.navigate(["admin/client"]);
        break;
    }
  }

  loadInvoices(): void {
    const payload = {
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      filters: {
        clientId: this.clientId,
        ...this.filters,
        status: this.filters.status
          ? (this.filters.status as { value: string }).value
          : null,
      },
      sortField: this.sortField,
      sortOrder: this.sortOrder,
    };

    this.isLoading = true;
    this.invoiceService.getPagedInvoices(payload).subscribe({
      next: (res: any) => {
        if (res && res.length > 0) {
          this.invoiceData = res[0].data;
          this.total = res[0].metadata[0]?.total || 0;
        } else {
          this.invoiceData = [];
          this.total = 0;
        }
      },
      error: (error) => {
        console.error("Error fetching invoices:", error);
        this.alerts
          .open("Failed to fetch invoices", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  stringify(item: { status: string; value: string } | null): string {
    return item ? item.status : "";
  }

  applyFilters(newFilters: any): void {
    this.filters = { ...this.filters, ...newFilters };
    this.pageIndex = 1;
    this.loadInvoices();
  }

  onEnter(event: KeyboardEvent): void {
    if (event.key === "Enter") {
      this.applyFilters({ searchText: this.filters.searchText });
    }
  }

  clearSearch(): void {
    this.filters.searchText = "";
    this.filters.status = null;
    this.applyFilters({ searchText: "", status: null });
  }

  onPageChange(page: number): void {
    this.pageIndex = page;
    this.loadInvoices();
  }

  getPageCount(): number {
    return Math.ceil(this.total / this.pageSize);
  }

  protected activeItemIndex = 0;

  protected onClick(item: string): void {
    this.alerts.open(item).subscribe();
  }

  resetUserPassword(email: any): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    const payload = {
      email: email,
    };
    this.resetPasswordService.requestResetPasswordClient(payload).subscribe({
      next: (response) => {
        this.alerts
          .open("Password reset information sent to client's email", {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
      },
      error: (error) => {
        console.error("Error resetting password:", error);
      },
    });
  }

  toggleActive(): void {
    this.isLoading = true;
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    if (!this.client) return;

    if (!this.client.active) {
      if (this.client.expiryDate) {
        const currentDate = new Date();
        const expiryDate = new Date(this.client.expiryDate);
        
        if (expiryDate < currentDate) {
          this.alerts
            .open("Cannot reactivate client. The expiry date has passed. Use Record renewal to extend membership and reactivate.", {
              label: "Warning",
              appearance: "warning",
            })
            .subscribe();
          this.isLoading = false;
          return;
        }
      }
    }

    const updatedClient = { ...this.client, active: !this.client.active };

    this.clientService.updateClient(updatedClient).subscribe({
      next: (response) => {
        this.client = response;
        const status = updatedClient.active ? "activated" : "deactivated";
        this.alerts
          .open(`Client has been ${status} successfully.`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadClientData();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error updating client data:", error);
        this.alerts
          .open("Failed to update client data.", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }

  openChangeClientType(): void {
    if (!this.loggedInUserCanEdit || !this.client || this.client.archived) {
      return;
    }
    this.open = false;
    this.changeTypeDialogOpen = true;
  }

  onChangeTypeConfirm(targetType: string): void {
    if (!this.client?._id || this.changeTypeBusy) return;
    this.changeTypeBusy = true;
    this.typeChangeFlow.execute(String(this.client._id), targetType).subscribe({
      next: (res) => {
        this.changeTypeBusy = false;
        this.changeTypeDialogOpen = false;
        if (res?.success && res.data) {
          this.typeChangeFlow.showSuccess(
            res.data.message ||
              "Previous client archived. Complete the new client record.",
          );
          this.typeChangeFlow.navigateToAddForm(res);
        } else {
          this.typeChangeFlow.showError(
            res?.message || "Failed to prepare client type change.",
          );
        }
      },
      error: (err) => {
        this.changeTypeBusy = false;
        this.typeChangeFlow.showError(
          err?.error?.message ||
            err?.message ||
            "Failed to prepare client type change.",
        );
      },
    });
  }

  onChangeTypeCancel(): void {
    this.changeTypeDialogOpen = false;
  }

  readonly canUnarchiveClient = canUnarchiveClient;

  toggleArchive(): void {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isLoading = true;
    if (!this.client) return;

    if (this.client.archived && !canUnarchiveClient(this.client)) {
      this.isLoading = false;
      this.alerts
        .open(TYPE_CHANGE_UNARCHIVE_MESSAGE, {
          label: "Cannot unarchive",
          appearance: "warning",
        })
        .subscribe();
      return;
    }

    const updatedClient = { ...this.client, archived: !this.client.archived };

    this.clientService.updateClient(updatedClient).subscribe({
      next: (response) => {
        this.client = response;
        const status = updatedClient.archived ? "archived" : "unarchived";
        this.alerts
          .open(`Client has been ${status} successfully.`, {
            label: "Success",
            appearance: "success",
          })
          .subscribe();
        this.loadClientData();
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        console.error("Error updating client data:", error);
        this.alerts
          .open(
            error?.error?.message ||
              error?.message ||
              "Failed to update client data.",
            {
              label: "Error",
              appearance: "error",
            },
          )
          .subscribe();
      },
    });
  }

  getShiftPageCount(): number {
    return Math.ceil(this.shiftTotal / this.shiftPageSize);
  }

  onShiftPageChange(page: number): void {
    this.shiftPageIndex = page;
    this.loadShifts()
  }

  getFormattedDate(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);

    if (isNaN(dateObj.getTime())) {
      return 'Invalid Date';
    }

    const day = dateObj.getDate();
    const monthNames = [
      "January", "February", "March", "April", "May", "June", "July",
      "August", "September", "October", "November", "December"
    ];
    const month = monthNames[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    const getOrdinalSuffix = (day: number): string => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };

    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
  }

  getMonth(date: Date): number {
    return new Date(date).getMonth() + 1; // getMonth() is zero-indexed, so add 1
  }

  getYear(date: Date): number {
    return new Date(date).getFullYear();
  }

  getChargesPageCount(): number {
    return Math.ceil(this.chargesTotal / this.chargesPageSize);
  }

  onChargesPageChange(page: number): void {
    this.chargesPageIndex = page;
    this.loadChargesRecords();
  }

  viewChargesDetails(clientId: any, month: any, year: any): void {
    this.router.navigate([`/invoice/${clientId}/${month}/${year}`]);
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("CLIENT_UPDATE");
  }

}
